import React, { useEffect, useState, useMemo } from "react";
import "../css/Profile.css";
import TaxRateData from "../tax_assets/us_tax_data.json";
import { ToastContainer, toast, Bounce } from "react-toastify";
import loadingAnimation from "../assets/Loading.json";
import Lottie from "lottie-react";
import AllAccounts from "./AllAccounts";
import unlockService from "../services/unlockService";
import BillingAddCardModal from "./BillingAddCardModal";
import { getProfile, updateProfile as apiUpdateProfile, getNotifications, updateNotifications } from "../api/profile";

const Profile = ({ username, setModalOpen }) => {
  const [currentProfile, setCurrentProfile] = useState();
  const [profile, setProfile] = useState({
    email: username,
    country: "US",
    filingStatus: "",
    state: "",
    incomeBracketFederalShortTermTaxRate: "",
    incomeBracketFederalLongTermTaxRate: "",
    federalLongTermTaxRate: 0,
    federalShortTermTaxRate: 0,
    incomeBracketStateTaxRate: "",
    stateTaxRate: 0,
    totalLongTermTaxRate: 0,
    totalShortTermTaxRate: 0,
    washSaleEnabled: true,
    washSaleMinAmount: 100,
    washSaleDay30: true,
    washSaleDay29: true,
    washSaleDay14: true,
    washSaleDay7: true,
    washSaleDay1: true,
    taxLossEnabled: true,
    taxLossMinAmount: 500,
    taxLossCadence: 'Daily',
    featureUpdates: true,
    taxUpdates: true,
    blogUpdates: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    activeAccounts: 0,
    activeInvestments: 0,
    taxSavings: 0,
  });
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'profile';
  });
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  useEffect(() => {
    // Don't fetch if username is null or undefined
    if (!username) {
      setLoading(false);
      setError("Please log in to view your profile.");
      return;
    }

    const fetchProfile = async () => {
      try {
        const raw = await getProfile();
        if (!raw) {
          setError("Failed to fetch profile.");
          return;
        }
        const data = { ...raw };
        console.log("[Profile] raw API data:", data);
        if (data.federalLongTermTaxRate) {
          data.federalLongTermTaxRate = parseFloat(data.federalLongTermTaxRate) / 100;
        }
        if (data.federalShortTermTaxRate) {
          data.federalShortTermTaxRate = parseFloat(data.federalShortTermTaxRate) / 100;
        }
        if (data.stateTaxRate) {
          data.stateTaxRate = parseFloat(data.stateTaxRate) / 100;
        }
        setCurrentProfile(prev => ({ ...prev, ...data }));
        setProfile(prev => ({ ...prev, ...data }));
      } catch (err) {
        console.error("[Profile] fetch error:", err);
        setError("Failed to fetch profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      setActiveTab(hash || 'profile');
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    // Don't fetch if username is null or undefined
    if (!username) {
      return;
    }

    async function fetchNotificationSettings() {
      try {
        const res = await getNotifications();
        if (res) {
          setProfile(prev => ({ ...prev, ...res }));
        }
      } catch (e) {
        // fallback to defaults
      }
    }
    fetchNotificationSettings();
  }, [username]);

  useEffect(() => {
    if (activeTab !== 'billing' || !username) return;
    let cancelled = false;
    setBillingLoading(true);
    (async () => {
      try {
        const [method, history] = await Promise.all([
          unlockService.getPaymentMethod(),
          unlockService.getPaymentHistory(),
        ]);
        if (!cancelled) {
          setPaymentMethod(method);
          setPaymentHistory(Array.isArray(history) ? history : (history?.payments ?? history ?? []));
        }
      } catch (e) {
        if (!cancelled) {
          setPaymentMethod(null);
          setPaymentHistory([]);
        }
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, username]);

  const renderFederalShortTermTaxBrackets = useMemo(() => {
    return TaxRateData.federal.short_term?.[profile.filingStatus]?.map(
      (range) => {
        return (
          <option
            selected={
              profile.incomeBracketFederalShortTermTaxRate ==
              `${range.min}-${range.max}`
            }
            key={`federal_short_term_${profile.filingStatus}_${range.max}`}
            value={`${range.min}-${range.max}|${range.rate}`}
          >
            {range.max === "Infinity"
              ? `Greater than $${range.min}`
              : `$${range.min} - $${range.max}`}
            &nbsp;({(range.rate * 100).toFixed(2)}%)
          </option>
        );
      }
    );
  }, [profile.filingStatus]);

  const renderFederalLongTermTaxBrackets = useMemo(() => {
    return TaxRateData.federal.long_term?.[profile.filingStatus]?.map(
      (range) => (
        <option
          selected={
            profile.incomeBracketFederalLongTermTaxRate ==
            `${range.min}-${range.max}`
          }
          key={`federal_long_term_${profile.filingStatus}_${range.max}`}
          value={`${range.min}-${range.max}|${range.rate}`}
        >
          {range.max === "Infinity"
            ? `Greater than $${range.min}`
            : `$${range.min} - $${range.max}`}
          &nbsp;({(range.rate * 100).toFixed(2)}%)
        </option>
      )
    );
  }, [profile.filingStatus]);

  const renderStateTaxBrackets = useMemo(() => {
    return (
      TaxRateData.states[profile.state]?.[profile.filingStatus]?.map(
        (range) => (
          <option
            selected={
              profile.incomeBracketStateTaxRate == `${range.min}-${range.max}`
            }
            key={`${profile.state}_${profile.filingStatus}_${range.min}-${range.max}`} // Unique key
            value={`${range.min}-${range.max}|${range.rate}`} // Store both income range and rate
          >
            {range.max === "Infinity"
              ? `Greater than $${range.min}`
              : `$${range.min} - $${range.max}`}
            &nbsp;({(range.rate * 100).toFixed(2)}%)
          </option>
        )
      ) || []
    ); // Return empty array if no data exists to prevent errors
  }, [profile.filingStatus, profile.state]);

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh",
        }}
      >
        <Lottie
          animationData={loadingAnimation}
          style={{ height: "150px", width: "150px" }} // Adjust loader size
        />
      </div>
    );
  if (error)
    return (
      <div className="dashboard-container dashboard-container--no-accounts">
        <div className="content-wrapper">
          <p>We could not find a profile for this user.</p>
          <p>Click the button below to create your profile.</p>
          <div className="primary-cta-wrapper-dashboard">
            <div className="tooltip-container">
              <button
                className="cta-button"
                onClick={() => setModalOpen({ open: true, type: "profile" })}
              >
                Create Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  if (!profile) return <p>No profile found.</p>;

  const validateProfile = () => {
    if (
      !profile.email
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      return false;
    }
    if (profile.filingStatus === "") {
      return false;
    }
    if (profile.incomeBracketFederalLongTermTaxRate === "") {
      return false;
    }
    if (profile.incomeBracketFederalShortTermTaxRate === "") {
      return false;
    }
    if (profile.incomeBracketStateTaxRate === "") {
      return false;
    }
    if (profile.state === "") {
      return false;
    }
    return true;
  };

  const submitTaxRateInfo = async () => {
    try {
      const response = await apiUpdateProfile({
        username,
        email: profile.email,
        filingStatus: profile.filingStatus,
        state: profile.state,
        incomeBracketFederalShortTermTaxRate: profile.incomeBracketFederalShortTermTaxRate,
        federalShortTermTaxRate: Number(profile.federalShortTermTaxRate) * 100,
        incomeBracketFederalLongTermTaxRate: profile.incomeBracketFederalLongTermTaxRate,
        federalLongTermTaxRate: Number(profile.federalLongTermTaxRate) * 100,
        incomeBracketStateTaxRate: profile.incomeBracketStateTaxRate,
        stateTaxRate: Number(profile.stateTaxRate) * 100,
        totalShortTermTaxRate: (Number(profile.federalShortTermTaxRate) + Number(profile.stateTaxRate)) * 100,
        totalLongTermTaxRate: (Number(profile.federalLongTermTaxRate) + Number(profile.stateTaxRate)) * 100,
      });

      toast.success("Profile updated successfully", {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });

      console.log(response);
    } catch (err) {
      console.error("Error submitting tax rate info:", err);
    }
  };

  return (
    <div className="container margin-top-20">
      <div className="row gutters-sm">
        <div className="col-md-4 d-none d-md-block">
          <div className="card">
            <div className="card-body">
              <nav className="nav flex-column nav-pills nav-gap-y-1">
                <a
                  href="#profile"
                  data-toggle="tab"
                  className={`nav-item nav-link has-icon nav-link-faded${activeTab === 'profile' ? ' active' : ''}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    className="feather feather-user mr-2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Profile Information
                </a>
                <a
                  href="#accounts"
                  data-toggle="tab"
                  className={`nav-item nav-link has-icon nav-link-faded${activeTab === 'accounts' ? ' active' : ''}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    className="feather feather-folder mr-2"
                  >
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  </svg>
                  All Accounts
                </a>
                <a
                  href="#notification"
                  data-toggle="tab"
                  className={`nav-item nav-link has-icon nav-link-faded${activeTab === 'notification' ? ' active' : ''}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    className="feather feather-bell mr-2"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  Notification
                </a>
                <a
                  href="#billing"
                  data-toggle="tab"
                  className={`nav-item nav-link has-icon nav-link-faded${activeTab === 'billing' ? ' active' : ''}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    className="feather feather-credit-card mr-2"
                  >
                    <rect
                      x="1"
                      y="4"
                      width="22"
                      height="16"
                      rx="2"
                      ry="2"
                    ></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                  Billing
                </a>
              </nav>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card">
            <div className="card-header border-bottom mb-3 d-flex d-md-none">
              <ul
                className="nav nav-tabs card-header-tabs nav-gap-x-1"
                role="tablist"
              >
                <li className="nav-item">
                  <a
                    href="#profile"
                    data-toggle="tab"
                    className={`nav-link has-icon${activeTab === 'profile' ? ' active' : ''}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="feather feather-user"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#notification"
                    data-toggle="tab"
                    className={`nav-link has-icon${activeTab === 'notification' ? ' active' : ''}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="feather feather-bell"
                    >
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#billing"
                    data-toggle="tab"
                    className={`nav-link has-icon${activeTab === 'billing' ? ' active' : ''}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="feather feather-credit-card"
                    >
                      <rect
                        x="1"
                        y="4"
                        width="22"
                        height="16"
                        rx="2"
                        ry="2"
                      ></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                  </a>
                </li>
              </ul>
            </div>
            <div className="card-body tab-content">
              <div className={`tab-pane${activeTab === 'profile' ? ' active' : ''}`} id="profile">
                <h6>YOUR PROFILE INFORMATION</h6>
                <hr />
                <form>
                  <div className="form-group">
                    <label for="fullName">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      id="fullName"
                      aria-describedby="EmailID"
                      placeholder="Enter your fullname"
                      value={profile.email}
                      onChange={(e) => {
                        setProfile({
                          ...profile,
                          email: e.target.value,
                        });
                      }}
                    />
                    <small id="fullNameHelp" className="form-text text-muted">
                      Your name may appear around here where you are mentioned.
                      You can change or remove it at any time.
                    </small>
                  </div>
                  <div className="form-group nice-form-group">
                    <label for="bio">Country</label>
                    <select
                      value={profile.country}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          country: e.target.value,
                        })
                      }
                    >
                      <option value="US">US</option>
                    </select>
                  </div>

                  <div className="form-group nice-form-group">
                    <label>Filing Status</label>
                    <select
                      value={profile.filingStatus}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          incomeBracketFederalShortTermTaxRate: "",
                          incomeBracketFederalLongTermTaxRate: "",
                          incomeBracketStateTaxRate: "",
                          federalLongTermTaxRate: 0,
                          federalShortTermTaxRate: 0,
                          stateTaxRate: 0,
                          filingStatus: e.target.value,
                        })
                      }
                    >
                      <option disabled value="">
                        {" "}
                        -- select an option --{" "}
                      </option>
                      <option value="single">Single</option>
                      <option value="married_filing_jointly">
                        Married jointly
                      </option>
                      <option value="married_filing_separately">
                        Married separately
                      </option>
                      <option value="head_of_household">
                        Head of household
                      </option>
                    </select>
                  </div>

                  <div className="form-group nice-form-group">
                    <label>
                      Income Bracket for Short Term Federal Tax Rate
                    </label>
                    <select
                      onChange={(e) => {
                        const [incomeBracket, taxRate] =
                          e.target.value.split("|");
                        setProfile({
                          ...profile,
                          incomeBracketFederalShortTermTaxRate: incomeBracket,
                          federalShortTermTaxRate: parseFloat(taxRate),
                        });
                      }}
                    >
                      <option value=""> -- select an option -- </option>
                      {renderFederalShortTermTaxBrackets}
                    </select>
                  </div>

                  <div className="form-group nice-form-group">
                    <label>Income Bracket for Long Term Federal Tax Rate</label>
                    <select
                      onChange={(e) => {
                        const [incomeBracket, taxRate] =
                          e.target.value.split("|");
                        setProfile({
                          ...profile,
                          incomeBracketFederalLongTermTaxRate: incomeBracket,
                          federalLongTermTaxRate: parseFloat(taxRate),
                        });
                      }}
                    >
                      <option value=""> -- select an option -- </option>
                      {renderFederalLongTermTaxBrackets}
                    </select>
                  </div>

                  <div className="form-group nice-form-group">
                    <p>
                      Your calculated long term federal tax rate:{" "}
                      {profile.federalLongTermTaxRate * 100}%
                    </p>
                  </div>

                  <div className="form-group nice-form-group">
                    <p>
                      Your calculated short term federal tax rate:{" "}
                      {profile.federalShortTermTaxRate * 100}%
                    </p>
                  </div>

                  <div className="form-group nice-form-group">
                    <label>State</label>
                    <select
                      value={profile.state}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          stateTaxRate: 0,
                          incomeBracketStateTaxRate: "",
                          state: e.target.value,
                        })
                      }
                    >
                      <option disabled value="">
                        {" "}
                        -- select an option --{" "}
                      </option>
                      {Object.keys(TaxRateData.states)
                        .sort()
                        .map((state) => {
                          return (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  <div className="form-group nice-form-group">
                    <label>Income Bracket for State Tax Rate</label>
                    <select
                      onChange={(e) => {
                        const [incomeBracket, taxRate] =
                          e.target.value.split("|");
                        setProfile({
                          ...profile,
                          incomeBracketStateTaxRate: incomeBracket,
                          stateTaxRate: parseFloat(taxRate),
                        });
                      }}
                    >
                      <option value=""> -- select an option -- </option>{" "}
                      {/* Ensure this option exists */}
                      {renderStateTaxBrackets}
                    </select>
                  </div>

                  <div className="form-group nice-form-group">
                    <p>
                      Your calculated state tax rate:{" "}
                      {profile.stateTaxRate * 100}%
                    </p>
                  </div>

                  <div className="form-group nice-form-group">
                    <p>
                      Your calculated total effective long term tax rate:{" "}
                      {profile.federalLongTermTaxRate * 100 +
                        profile.stateTaxRate * 100}
                      %
                    </p>
                  </div>
                  <div className="form-group nice-form-group">
                    <p>
                      Your calculated total effective short term tax rate:{" "}
                      {profile.federalShortTermTaxRate * 100 +
                        profile.stateTaxRate * 100}
                      %
                    </p>
                  </div>

                  {/* <div className="form-group">
                <label for="location">Location</label>
                <input type="text" className="form-control" id="location" placeholder="Enter your location" value="Bay Area, San Francisco, CA" />
              </div> */}
                  {/* <div className="form-group small text-muted">
                All of the fields on this page are optional and can be deleted at any time, and by filling them out, you're giving us consent to share this data wherever your user profile appears.
              </div> */}
                  <button
                    type="button"
                    onClick={submitTaxRateInfo}
                    className="btn btn-primary"
                    disabled={!validateProfile()}
                  >
                    Update Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProfile({ ...currentProfile });
                    }}
                    className="btn btn-light"
                  >
                    Reset Changes
                  </button>
                </form>
              </div>
              <div className={`tab-pane${activeTab === 'accounts' ? ' active' : ''}`} id="accounts">
                <AllAccounts setModalOpen={setModalOpen} username={username} />
              </div>
              <div className={`tab-pane${activeTab === 'notification' ? ' active' : ''}`} id="notification">
                <h6>NOTIFICATION SETTINGS</h6>
                <hr />
                <form>
                  {/* --- Wash Sale Avoidance Alerts --- */}
                  <div className="form-group mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="mb-0 font-weight-bold">Wash Sale Avoidance Alerts</label>
                      <div className="custom-control custom-switch profitree-toggle">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="ws-alerts-toggle"
                          checked={profile?.washSaleEnabled ?? true}
                          onChange={e => setProfile({ ...profile, washSaleEnabled: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="ws-alerts-toggle"></label>
                      </div>
                    </div>
                    <small className="form-text text-muted mb-2">
                      Get notified when you are approaching a wash sale window for significant amounts.
                    </small>
                    <div className={`row align-items-center${profile?.washSaleEnabled === false ? ' notification-disabled' : ''}`}>
                      <div className="col-md-7 mb-2 mb-md-0">
                        <div className="mb-1">Alert me when avoidance window is:</div>
                        <div className="d-flex flex-wrap align-items-center">
                          {[30,29,14,7,1].map(day => {
                            const dayProp = `washSaleDay${day}`;
                            return (
                              <div className="custom-control custom-checkbox mr-3 mb-1" key={day}>
                                <input
                                  type="checkbox"
                                  className="custom-control-input profitree-toggle"
                                  id={`ws-day-${day}`}
                                  checked={profile?.[dayProp] ?? true}
                                  onChange={e => setProfile({ ...profile, [dayProp]: e.target.checked })}
                                />
                                <label className="custom-control-label" htmlFor={`ws-day-${day}`}>{day} day{day !== 1 ? 's' : ''} before</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="col-md-5">
                        <label className="mb-1">Minimum Amount ($)</label>
                        <input
                          type="number"
                          className="form-control profitree-input"
                          min={0}
                          value={profile?.washSaleMinAmount ?? 100}
                          onChange={e => setProfile({ ...profile, washSaleMinAmount: Number(e.target.value) })}
                          style={{ maxWidth: 120 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* --- Tax Loss Harvesting Alerts --- */}
                  <div className="form-group mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="mb-0 font-weight-bold">Tax Loss Harvesting Alerts</label>
                      <div className="custom-control custom-switch profitree-toggle">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="tlh-alerts-toggle"
                          checked={profile?.taxLossEnabled ?? true}
                          onChange={e => setProfile({ ...profile, taxLossEnabled: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="tlh-alerts-toggle"></label>
                      </div>
                    </div>
                    <small className="form-text text-muted mb-2">
                      Get notified when you have tax loss harvesting opportunities above a certain amount.
                    </small>
                    <div className={`row align-items-center${profile?.taxLossEnabled === false ? ' notification-disabled' : ''}`}>
                      <div className="col-md-6 mb-2 mb-md-0">
                        <label className="mb-1">Minimum Amount ($)</label>
                        <input
                          type="number"
                          className="form-control profitree-input"
                          min={0}
                          value={profile?.taxLossMinAmount ?? 500}
                          onChange={e => setProfile({ ...profile, taxLossMinAmount: Number(e.target.value) })}
                          style={{ maxWidth: 120 }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="mb-1">Alert Cadence</label>
                        <select
                          className="form-control profitree-input"
                          value={profile?.taxLossCadence ?? 'Daily'}
                          onChange={e => setProfile({ ...profile, taxLossCadence: e.target.value })}
                          style={{ maxWidth: 180 }}
                        >
                          <option value="Daily">Daily</option>
                          <option value="Weekly">Weekly</option>
                          <option value="Monthly">Monthly</option>
                          <option value="opportunity">Only when new opportunity appears</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* --- Other Notification Toggles --- */}
                  <div className="form-group mb-4">
                    <label className="mb-2 font-weight-bold">Other Notifications</label>
                    <div className="d-flex flex-wrap align-items-center">
                      <div className="custom-control custom-switch mr-4 mb-2 profitree-toggle">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="feature-updates-toggle"
                          checked={profile?.featureUpdates ?? true}
                          onChange={e => setProfile({ ...profile, featureUpdates: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="feature-updates-toggle">
                          Feature Updates
                        </label>
                      </div>
                      <div className="custom-control custom-switch mr-4 mb-2 profitree-toggle">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="tax-updates-toggle"
                          checked={profile?.taxUpdates ?? true}
                          onChange={e => setProfile({ ...profile, taxUpdates: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="tax-updates-toggle">
                          Tax Updates
                        </label>
                      </div>
                      <div className="custom-control custom-switch mb-2 profitree-toggle">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="blog-updates-toggle"
                          checked={profile?.blogUpdates ?? true}
                          onChange={e => setProfile({ ...profile, blogUpdates: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="blog-updates-toggle">
                          Blog Updates
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-start mt-4">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={async () => {
                        try {
                          await updateNotifications({
                            washSaleEnabled: profile.washSaleEnabled,
                            washSaleDay30: profile.washSaleDay30,
                            washSaleMinAmount: profile.washSaleMinAmount,
                            taxLossEnabled: profile.taxLossEnabled,
                            taxLossMinAmount: profile.taxLossMinAmount,
                            taxLossCadence: profile.taxLossCadence,
                            featureUpdates: profile.featureUpdates,
                            taxUpdates: profile.taxUpdates,
                            blogUpdates: profile.blogUpdates,
                          });
                          toast.success('Notification preferences saved!', {
                            position: 'bottom-center',
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: 'light',
                          });
                        } catch (err) {
                          toast.error('Failed to save notification preferences.', {
                            position: 'bottom-center',
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: 'light',
                          });
                        }
                      }}
                    >
                      Update Preferences
                    </button>
                  </div>
                </form>
              </div>
              <div className={`tab-pane${activeTab === 'billing' ? ' active' : ''}`} id="billing">
                <h6>BILLING SETTINGS</h6>
                <hr />
                <form>
                  <div className="form-group">
                    <label className="d-block mb-0">Payment Method</label>
                    {billingLoading ? (
                      <div className="small text-muted">Loading...</div>
                    ) : paymentMethod && (paymentMethod.last4 || paymentMethod.last4 === 0) ? (
                      <div className="small text-muted">
                        {paymentMethod.brand ? `${String(paymentMethod.brand).charAt(0).toUpperCase() + String(paymentMethod.brand).slice(1)} ` : ''}
                        ending in {paymentMethod.last4}
                        {paymentMethod.exp_month && paymentMethod.exp_year ? (
                          <span> · Expires {String(paymentMethod.exp_month).padStart(2, '0')}/{paymentMethod.exp_year}</span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="small text-muted">
                        You have not added a payment method
                      </div>
                    )}
                  </div>
                  <div className="form-group mb-0">
                    <label className="d-block">Payment History</label>
                    <div className="border border-gray-500 bg-gray-200 p-3 font-size-sm">
                      {billingLoading ? (
                        <div className="text-center">Loading...</div>
                      ) : paymentHistory.length === 0 ? (
                        <div className="text-center">You have not made any payment.</div>
                      ) : (
                        <ul className="list-unstyled mb-0">
                          {paymentHistory.map((p, i) => (
                            <li key={p.id || i} className="d-flex justify-content-between py-2 border-bottom border-gray-400">
                              <span>
                                {p.description || p.status || 'Payment'}
                                {p.created != null && (
                                  <span className="text-muted ml-2">
                                    {typeof p.created === 'number'
                                      ? new Date(p.created * 1000).toLocaleDateString()
                                      : new Date(p.created).toLocaleDateString()}
                                  </span>
                                )}
                              </span>
                              <span>
                                {p.amount != null && (
                                  <>${(Math.abs(p.amount) / 100).toFixed(2)} {p.currency || 'USD'}</>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BillingAddCardModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={async () => {
          try {
            const [method, history] = await Promise.all([
              unlockService.getPaymentMethod(),
              unlockService.getPaymentHistory(),
            ]);
            setPaymentMethod(method);
            setPaymentHistory(Array.isArray(history) ? history : (history?.payments ?? history ?? []));
            toast.success('Payment method added.');
          } catch (e) {
            toast.error('Could not refresh payment method.');
          }
        }}
      />
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

export default Profile;
